import { useEffect, useState } from "react";
import corporateAccountService from "../services/corporateAccountService";
import locationsService from "../services/locationsService";
import { CorporateAccountTotalVisits } from "../models/corporateAccountTotalVisitsModel";
import { useNavigate } from "react-router-dom";
import MDBox from "mdComponents/MDBox";
import MDTypography from "mdComponents/MDTypography";
import { Card } from "@mui/material";
import { useUser } from "../context/UserContext";
import { useHostPortalContext } from "../context/HostPortalContext";
import { useAlertContext } from "context/AlertContext";
import LoadingSpinner from "./LoadingSpinner";

interface CorporateVisitsProps {
  limitToId?: number;
  hideBusinessName?: boolean;
  title?: string;
  description?: string;
  disableClick?: boolean;
}

const CorporateVisits = (props: CorporateVisitsProps) => {
  const { limitToId, description, title, hideBusinessName, disableClick } = props;
  const navigate = useNavigate();
  const { setAlert } = useAlertContext();
  const { userCorporateAccount } = useUser();
  const { setHost } = useHostPortalContext();

  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [visitsData, setVisitsData] = useState<CorporateAccountTotalVisits[]>([]);
  const [showCorporateRollup, setShowCorporateRollup] = useState(true);

  useEffect(() => {

    const loadVisits = async () => {
      try {
        let data = await corporateAccountService.getCorporateVisits(
          userCorporateAccount?.id
        );

        if (data) {

        if(limitToId){
                data = data.filter(a => a.hostId === limitToId);
        };
    
          setVisitsData(data);

          // hide corporate total if only 1 location
          setShowCorporateRollup(data.filter(a => a.hostId !== -1).length > 1);
        } else {
          setAlert("error", "error", "Unable to load all KPIs");
        }
      } catch (err) {
        console.error(err);
          setAlert("error", "error", "Unable to load visits");
      } finally {
        setLoading(false);
      }
    };

    loadVisits();
  }, []);

  const toGoLocationActivity = async(hostId: number) => {
    if (disableClick || hostId === -1) return;

    setRedirecting(true);

    const host = await locationsService.getHostRecord(hostId);
    if (!host) {
      setRedirecting(false);
      return;
    }

    setHost(host);
    setRedirecting(false);

    navigate(`/location/activity/${hostId}`);
  };

  const renderCard = (item: CorporateAccountTotalVisits) => {
    const isCorporateRow = item.hostId === -1;

    if (isCorporateRow && !showCorporateRollup) return null;

    return (
      <Card
        key={item.hostId}
        onClick={() => toGoLocationActivity(item.hostId)}
        sx={{
          mb: 2,
          cursor: "pointer",
          padding: 2,
          border: isCorporateRow ? "2px solid #21b2a6" : "1px solid #ddd",
          background: isCorporateRow ? "#e8fffd" : "#fff"
        }}
      >
        <MDTypography variant="h6" fontWeight="bold">
          {isCorporateRow ? "Corporate Total" : item.hostName}
        </MDTypography>

        <MDBox display="flex" justifyContent="space-between" mt={2}>
          <MDBox textAlign="center">
            <MDTypography variant="h5">{item.totalVisits}</MDTypography>
            <MDTypography variant="caption">Total</MDTypography>
          </MDBox>

          <MDBox textAlign="center">
            <MDTypography variant="h5">{item.visitsThisWeek}</MDTypography>
            <MDTypography variant="caption">This Week</MDTypography>
          </MDBox>

          <MDBox textAlign="center">
            <MDTypography variant="h5">{item.visitsThisMonth}</MDTypography>
            <MDTypography variant="caption">This Month</MDTypography>
          </MDBox>

          <MDBox textAlign="center">
            <MDTypography variant="h5">{item.visitsThisYear}</MDTypography>
            <MDTypography variant="caption">This Year</MDTypography>
          </MDBox>
        </MDBox>
      </Card>
    );
  };
//text spacing class
  if (loading) return <LoadingSpinner title="Loading Visits..." />;
  return (
    <MDBox>
      <MDTypography variant="h5" fontWeight="bold">
        {title ?? 'Location User Visits'}
      </MDTypography>
        
       <MDTypography variant="button" color="text" mb={1} sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}> 
        {description ?? 'Visits recorded at each location. Click each location to view recent visits.'}
      </MDTypography>
      <MDBox>
        {visitsData.map(item => renderCard(item))}
      </MDBox>
    </MDBox>
  );
};

export default CorporateVisits;
